import React, { CSSProperties, ChangeEvent, ReactNode, useState } from 'react';
import { formatCurrencySecond, formatDate, formatSecondsToDuration, mascaraCnpjCpf, maskPercentageSecond } from '@/helpers/formaters';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { PaginationParameters } from '@/types/paginationProps';
import { LinearProgressWithLabel } from '@/components/LinearProgressWithLabel/page';
import { BulkAction, RowData, TableColumn } from './types/collapsibleTable';
import { Button, Checkbox, CircularProgress, Pagination, Stack } from '@mui/material';
import { DropdownUserList } from '../../DropdownUserList/DropdownUserList';
import { calculateTotalPages } from '@/helpers/tableHelpers';
import { IconButton } from '@mui/joy';
import { useCollapsibleSelection } from './_hooks/useCollapsibleSelection'
import { NotFoundMessage } from '@/components/NotFoundMessage';
import ZoomImage from '@/components/ZoomImage/ZoomImage';
import Image from 'next/image';
import { ITableAction } from '../types/tableProps'
import { TableActions } from '../TableActions/TableActions';
import clsx from 'clsx';

interface CollapsibleTableProps {
    columns: TableColumn[];
    detailColumns: TableColumn[];
    detailColumnsAmount?: TableColumn[];
    data: RowData[];
    keyChildren: string;
    keyItem: string;
    tableActions?: ITableAction;
    tableActionsAmount?: ITableAction;
    detailsActions?: ITableAction;
    setCurrentRow: any;
    setCurrentDetailRow: any;
    setPaginationParams: (update: (prev: any) => any) => void;
    paginationParams: PaginationParameters;
    loading?: boolean;
    keyChildrenItem?: string;
    selectable?: boolean;
    selectedRows?: RowData[];
    selectedIds?: string[];
    selectedChilds?: any[];
    selectedChildIds?: string[];
    onSelectionChange?: (ids: string[], detailIds: string[], selectedRows: any[], selectedChilds: any[]) => void;
    isRowSelectable?: (row: RowData) => boolean;
    isChildRowSelectable?: (detail: any) => boolean;
    bulkActions?: BulkAction[];
    allowSelectParentWithoutChildren?: boolean;
}

export const CollapsibleTable: React.FC<CollapsibleTableProps> = ({
    columns,
    detailColumns,
    data,
    keyChildren,
    keyItem,
    detailsActions,
    tableActions,
    setCurrentRow,
    setCurrentDetailRow,
    detailColumnsAmount,
    tableActionsAmount,
    paginationParams,
    setPaginationParams,
    keyChildrenItem,
    loading,
    selectable = false,
    selectedIds = [],
    selectedChildIds = [],
    onSelectionChange,
    isRowSelectable = () => true,
    isChildRowSelectable = () => true,
    bulkActions = [],
    allowSelectParentWithoutChildren = false,
}) => {
    const [openRow, setOpenRow] = useState<number | null>(null);
    const [openActionIndex, setOpenActionIndex] = useState<{ rowId: number; detailIdx: number } | null>(null);
    const [openModalImage, setOpenModalImage] = useState(false);
    const [urlImage, setUrlImage] = useState("")

    const selection = useCollapsibleSelection({
        data,
        keyChildren,
        keyItem,
        keyChildrenItem,
        selectedIds,
        selectedChildIds,
        onSelectionChange,
        isRowSelectable,
        isChildRowSelectable,
        allowSelectParentWithoutChildren
    });

    const toggleRow = (id: number) => {
        setOpenRow((prev) => (prev === id ? null : id));
        setOpenActionIndex(null);
    };

    const handleSort = (key: string) => {
        setPaginationParams(prev => {
            const newDirection = prev.order === key && prev.direction === 'ASC' ? 'DESC' : 'ASC';
            return {
                ...prev,
                order: key,
                direction: newDirection,
            };
        });
    };

    const toggleAction = (rowId: number, detailIdx: number) => {
        const isOpen = openActionIndex?.rowId === rowId && openActionIndex?.detailIdx === detailIdx;
        setOpenActionIndex(isOpen ? null : { rowId, detailIdx });
    };

    function getStyleCell(col: TableColumn, value: any, detail: any) {
        let colorText = 'var(--text-color)';
        let fontWeight = 500;
        let backgroundColor = 'transparent';
        let fontSize = 12;
        let textAlign: 'left' | 'right' | 'center' = 'left';
        let celStyle: CSSProperties = col?.celStyle || {};
        let divStyle: CSSProperties = col?.colStyle || {};

        if (col.isImage) {
            let imageValue = value;

            if (col.key.includes('photoThumbnail')) {
                imageValue =
                    detail?.photo1 ||
                    detail?.photo2 ||
                    detail?.photo3 ||
                    detail?.photo;
            }

            if (!imageValue) {
                value = '-';
            } else {
                const size = col.imageSize || 48;

                value = (
                    <div className="relative w-[60px] h-[60px] rounded-full overflow-hidden bg-gray-100"
                        onClick={(e) => {
                            e.stopPropagation();
                            setUrlImage(imageValue || "");
                            setOpenModalImage(true)
                        }}
                    >
                        <Image
                            src={imageValue}
                            className='text-center cursor-pointer'
                            alt="Foto do anúncio"
                            priority={false}
                            quality={70}
                            fill
                        />
                    </div>
                );
            }

            textAlign = 'center';
            celStyle = {
                padding: 4,
            };
        }

        if (col?.isCurrency) {
            colorText = 'var(--primary-color) !important';
            fontWeight = 600
            value = formatCurrencySecond(value || 'R$ 0,00');
        }

        if (col?.isDate) {
            value = value ? formatDate(value) : '-';
        }

        if (col?.isDateAndHour) {
            value = value ? formatDate(value, true) : '-';
        }

        if (col?.isPercent) {
            value = value ? maskPercentageSecond(value) : '0 %';
        }

        if (col?.isDocument) {
            value = value ? mascaraCnpjCpf(value) : '-';
        }

        if (col.key.includes('status') && col?.keyColor) {
            backgroundColor = detail[col.keyColor];
            colorText = '#fff';
            fontSize = 10;
            textAlign = 'center'
        }

        if (col.keyColor) {
            backgroundColor = detail[col.keyColor];
            colorText = '#fff';
            fontSize = 10;
            textAlign = 'center';
            fontWeight = 600;
        }

        if (col.isLinear) {
            value = value ? <LinearProgressWithLabel value={value} /> : <LinearProgressWithLabel value={0} />
        }

        if (col.isDuration) {
            value = value ? formatSecondsToDuration(value) : '-';
        }

        if (col.isListUsers) {
            value = value.length > 0 ? <DropdownUserList users={value} /> : '-'
        }

        if (col.isBoolean) {
            backgroundColor = value ? "var(--primary-color)" : "var(--danger)",
                colorText = '#fff';
            textAlign = 'center';
            fontWeight = 600;
            fontSize = 10;
            celStyle = {
                width: 'fit-content',
                paddingRight: 20,
                paddingLeft: 20,
            }
            value = value ? "Sim" : "Não"
        }

        if (col.isNumber && typeof value == "number") {
            if (value < 0) {
                colorText = 'var(--danger)';
            }
        }

        if (col.isRank) {
            value = `${value}º`
        }

        if(col.render && detail){
            value = col.render(detail)
        }

        return {
            colorText,
            fontSize,
            celStyle,
            backgroundColor,
            valueFormatted: value,
            textAlign,
            fontWeight,
            divStyle
        }
    }

    function getListColumns(row: any) {
        if (row?.typeFormReceivable == 1 && detailColumnsAmount) {
            return detailColumnsAmount;
        } else {
            return detailColumns;
        }
    }

    function renderActions(row: any, index: number): ReactNode {
        let actions: ITableAction | null = null;

        if (row?.typeFormReceivable == 1 && tableActionsAmount) {
            actions = tableActionsAmount;
        } else if (tableActions) {
            actions = tableActions;
        }

        if (!actions) return null;

        return (
            <td className="px-1 py-1 max-w-[80px]">
                <div className="flex rounded items-center justify-center">
                    <TableActions
                        rowData={row}
                        tableActions={actions}
                        key={`actions-${index}`}
                    />
                </div>
            </td>
        );
    }

    if (loading) return (
        <div className='flex justify-center items-center min-h-[200px] w-full h-full'>
            <CircularProgress />
        </div>
    )

    if (!loading && !data.length) return (
        <NotFoundMessage
            className="min-h-[350px]"
            icon="assignment_late"
            title="Nenhum item encontrado"
            subtitle="Selecione um novo período ou altere os filtros de busca"
            sizeIcon="large"
        />
    )

    return (
        <div className="py-4 overflow-x-auto">

            {selectable && bulkActions.length > 0 && (
                <div className="flex w-full justify-between gap-2 mb-2">
                    <div className="flex gap-2 items-center text-sm ml-4 text-[--text-color] ">
                        <p>{selection.totalRowsSelected} linhas selecionadas</p>
                        {selection.totalRowsSelected > 0 ? (
                            <IconButton
                                size='sm'
                                onClick={() => selection.updateSelection([], [])}
                            >
                                <span className="material-symbols-outlined !text-[--danger] !text-[16px]">
                                    delete
                                </span>
                            </IconButton>
                        ) : null}
                    </div>
                    <div className='flex gap-2'>
                        {bulkActions
                            .filter(action => action.permission !== false)
                            .map((action, idx) => (
                                <Button
                                    key={idx}
                                    startIcon={action.icon}
                                    onClick={() => selection.executeBulkAction(action)}
                                    size="small"
                                    disabled={selection.isDisabledBulkActions}
                                    {...action.buttonProps}
                                    sx={{
                                        fontSize: 12,
                                        ...(action.buttonProps?.sx || {}),
                                        ...(selection.isDisabledBulkActions && {
                                            opacity: 0.6,
                                            cursor: 'default'
                                        }),
                                    }}
                                >
                                    {action.title}
                                </Button>

                            ))}
                    </div>
                </div>
            )}

            <table className="min-w-full text-sm bg-[--background-color] shadow rounded-md overflow-hidden">
                <thead>
                    <tr className="bg-[--background-color] text-gray-600 text-xs tracking-wider">
                        {selectable && (
                            <th className="px-2 py-3 text-center">
                                <Checkbox
                                    checked={selection.isSelectAllChecked()}
                                    indeterminate={selection.isSelectAllIndeterminate()}
                                    onChange={selection.handleSelectAllPage}
                                    inputProps={{ 'aria-label': 'selecionar todos' }}
                                    size="small"
                                />
                            </th>
                        )}
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                className={clsx(`px-6 py-3 text-${col.align || 'left'}`, col.sortable && "cursor-pointer")}
                                onClick={col.sortable ? () => handleSort(col.key) : undefined}
                            >
                                {col.label}
                                {paginationParams?.order === col.key && (
                                    paginationParams?.direction === 'ASC' ? (
                                        <ArrowUpwardIcon style={{ fontSize: 16 }} />
                                    ) : (
                                        <ArrowDownwardIcon style={{ fontSize: 16 }} />
                                    )
                                )}
                            </th>
                        ))}
                        {
                            tableActions &&
                            <th className="text-center px-6 py-3">Ações</th>
                        }
                    </tr>
                </thead>
                <tbody className="text-gray-800 divide-y divide-[--grey-color]">
                    {data && data.length > 0 && data.map((row, index) => (
                        <React.Fragment key={index}>
                            <tr
                                className={`transition-all hover:bg-[--grey-color] ${index % 2 !== 1 ? 'bg-[--grey-color]' : ''} ${openRow === row[keyItem] ? 'border-l-4 !border-l-[--primary-color]' : ''}`}
                                title='Clique para expandir'
                                onClick={() => setCurrentRow(row)}
                            >
                                {selectable && (
                                    <td className="px-2 py-4 text-center">
                                        <Checkbox
                                            checked={selection.isRowFullyChecked(row)}
                                            indeterminate={selection.isRowIndeterminate(row)}
                                            disabled={selection.isRowDisabled(row)}
                                            sx={{
                                                opacity: selection.isRowDisabled(row) ? 0.5 : 1,
                                            }}
                                            onChange={() =>
                                                row[keyChildren] && keyChildrenItem
                                                    ? selection.handleAllChildrenCheckbox(row)
                                                    : selection.handleRowCheckbox(row)
                                            }
                                            size="small"
                                        />
                                    </td>
                                )}
                                {columns.map((col) => {
                                    let value = row[col.key];
                                    const {
                                        backgroundColor,
                                        colorText,
                                        fontSize,
                                        valueFormatted,
                                        textAlign,
                                        fontWeight,
                                        divStyle,
                                        celStyle,
                                    } = getStyleCell(col, value, row);

                                    return (
                                        <td
                                            key={col.key}
                                            className="px-6 py-4 z-10 font-medium cursor-pointer transition"
                                            style={divStyle}
                                            onClick={() => {
                                                setCurrentDetailRow(row);
                                                toggleRow(row[keyItem]);
                                            }}
                                        >
                                            <div
                                                className="px-1 py-1 rounded text-center"
                                                style={{
                                                    backgroundColor: backgroundColor || 'transparent',
                                                    color: colorText,
                                                    fontSize: fontSize,
                                                    textAlign: textAlign || '',
                                                    fontWeight: fontWeight,
                                                    ...celStyle,
                                                }}
                                            >
                                                {valueFormatted}
                                            </div>
                                        </td>
                                    );
                                })}
                                {renderActions(row, index)}
                            </tr>

                            <tr>
                                <td
                                    colSpan={columns.length + (selectable ? 1 : 0) + 1}
                                    className={`px-6 py-0 ${openRow === row[keyItem]
                                        ? 'border-x-[1px] border-b-[1px] border-[--primary-color]'
                                        : ''}`}
                                >
                                    <div
                                        className={`transition-all duration-500 ease-in-out overflow-hidden ${openRow === row[keyItem]
                                            ? 'max-h-[500px] opacity-100 scale-100 py-4'
                                            : 'max-h-0 opacity-0 scale-95'}`}
                                    >
                                        <div className="bg-[--background-color] rounded-md overflow-auto max-h-[400px]">
                                            <table className="min-w-full text-sm bg-[--background-color] shadow rounded-md overflow-hidden">
                                                <thead className="bg-[--grey-color] text-[--text-color]">
                                                    <tr>
                                                        {selectable && keyChildrenItem && (
                                                            <th className="px-2 py-2 text-center"></th>
                                                        )}
                                                        {getListColumns(row).map((col) => (
                                                            <th
                                                                key={col.key}
                                                                className={`text-${col.align || 'left'} px-4 py-2`}
                                                            >
                                                                {col.label}
                                                            </th>
                                                        ))}
                                                        {detailsActions && (
                                                            <th className="text-center px-4 py-2">Ações</th>
                                                        )}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {row[keyChildren] &&
                                                        row[keyChildren].map((detail: any, idx: number) => (
                                                            <tr
                                                                key={idx}
                                                                className="border-t border-[--grey-border-light] relative z-10"
                                                            >
                                                                {selectable && keyChildrenItem && (
                                                                    <td className="px-2 py-4 text-center">
                                                                        <Checkbox
                                                                            checked={selection.controlledSelectedChildIds.includes(detail[keyChildrenItem])}
                                                                            disabled={!isChildRowSelectable(detail)}
                                                                            sx={{
                                                                                opacity: !isChildRowSelectable(detail) ? 0.5 : 1,
                                                                            }}
                                                                            onChange={() => selection.handleChildCheckbox(row, detail)}
                                                                            size="small"
                                                                        />
                                                                    </td>
                                                                )}
                                                                {getListColumns(row).map((col) => {
                                                                    let value = detail[col.key];
                                                                    const {
                                                                        backgroundColor,
                                                                        colorText,
                                                                        fontSize,
                                                                        valueFormatted,
                                                                        textAlign,
                                                                        fontWeight,
                                                                    } = getStyleCell(col, value, detail);

                                                                    return (
                                                                        <td key={col.key} className="px-4 py-5">
                                                                            <div
                                                                                className="px-1 py-1 rounded text-center"
                                                                                style={{
                                                                                    backgroundColor:
                                                                                        backgroundColor ||
                                                                                        'transparent',
                                                                                    color: colorText,
                                                                                    fontSize: fontSize,
                                                                                    textAlign: textAlign || '',
                                                                                    fontWeight,
                                                                                }}
                                                                            >
                                                                                {valueFormatted}
                                                                            </div>
                                                                        </td>
                                                                    );
                                                                })}
                                                                {detailsActions && (
                                                                    <td className="px-4 py-5 max-w-[80px] z-50">
                                                                        <div className="flex rounded items-center justify-center">
                                                                            <TableActions
                                                                                rowData={detail}
                                                                                tableActions={detailsActions}
                                                                                key={'actions-detail-row'}
                                                                            />
                                                                        </div>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </React.Fragment>
                    ))}
                </tbody>
            </table>

            {paginationParams.page && paginationParams.totalRows > paginationParams.records &&
                <div className="flex justify-between items-center w-full mt-4">
                    <p>
                        Exibindo página {paginationParams.page} de {calculateTotalPages(paginationParams.totalRows)} (total de{" "}
                        {paginationParams.totalRows} itens)
                    </p>

                    <Stack>
                        <Pagination
                            key={paginationParams.page}
                            defaultPage={1}
                            count={calculateTotalPages(paginationParams.totalRows)}
                            showFirstButton
                            showLastButton
                            onChange={(event: ChangeEvent<unknown>, newPage: number) => setPaginationParams(prev => ({ ...prev, page: newPage }))}
                            page={paginationParams.page}
                        />
                    </Stack>
                </div>
            }

            <ZoomImage
                url={urlImage ? urlImage : ""}
                openModal={openModalImage}
                setOpenModal={setOpenModalImage}
            />
        </div>
    );
};